import { Anchor, Span } from '@dnb/eufemia';
import styled from '@emotion/styled';
import { useNavHistory } from '@portals/shared-frontend/hooks';
import type { NavNode } from '@portals/shared-frontend/utils';
import React, { useCallback } from 'react';
import { Link } from 'react-router-dom';

import TypeBadge from './TypeBadge';

/**
 * RecentPagesTree Component
 *
 * Displays the full navigation tree in a nested, hierarchical structure.
 * Only shows branches that contain recently visited pages (nodes with lastVisited).
 */
export function RecentPagesTree({
  onNavigate,
}: {
  /** Callback when a page is clicked - receives the page path */
  onNavigate: (path: string) => void;
}) {
  const { buildTree } = useNavHistory();

  // Recursively render tree nodes (memoized to avoid recreation on every render)
  const renderNode = useCallback((node: NavNode, depth = 0): React.ReactNode => {
    // Skip root node
    if (node.path === '/') {
      return <div>{node.children.map((child) => renderNode(child, depth))}</div>;
    }

    const hasChildren = node.children.length > 0;

    return (
      <TreeNode $indented={depth > 0} key={node.path}>
        <NodeContent>
          <div className="flex items-center gap-1 h-6">
            <TypeBadge size="medium" type={node.type} />
            <Anchor
              element={Link}
              onClick={(e) => {
                e.preventDefault();
                onNavigate(node.path);
              }}
              to={node.path}
            >
              <Span size="medium">{node.name}</Span>
            </Anchor>
          </div>
        </NodeContent>
        {hasChildren && (
          <ChildrenContainer>{node.children.map((child) => renderNode(child, depth + 1))}</ChildrenContainer>
        )}
      </TreeNode>
    );
  }, []);
  // Build hierarchical tree from flat storage (optimized)
  const tree = buildTree();

  if (tree.children.length === 0) {
    return null;
  }

  return <div>{renderNode(tree)}</div>;
}

const TreeNode = styled.div<{ $indented: boolean }>`
  margin-bottom: 1rem;
  margin-left: ${({ $indented }) => ($indented ? '1.5rem' : 0)};
`;

const NodeContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ChildrenContainer = styled.div`
  border-left: 2px solid #e5e7eb;
  padding-left: 0.5rem;
  margin-top: 0.5rem;
`;
